import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import {
  LABEL_CLASS,
  SELECT_TRIGGER_CLASS,
} from "../../../common/CertificateOnboarding/utils/styles";

const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 6;
const VIEWPORT_PADDING = 8;

function CfFormSelect({
  id,
  label,
  value,
  onChange,
  options = [],
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuLayout, setMenuLayout] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const selected = options.find((opt) => opt.value === value) || options[0];

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow =
      window.innerHeight - rect.bottom - VIEWPORT_PADDING - MENU_GAP;
    const spaceAbove = rect.top - VIEWPORT_PADDING - MENU_GAP;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;

    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      openUpward ? spaceAbove : spaceBelow,
    );

    setMenuLayout({
      top: openUpward
        ? Math.max(VIEWPORT_PADDING, rect.top - MENU_GAP - maxHeight)
        : rect.bottom + MENU_GAP,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, maxHeight),
      openUpward,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();

    const handleScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);

    return () => {
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
    setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const openMenu = () => {
    if (disabled) return;
    const selectedIndex = options.findIndex((opt) => opt.value === value);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOpen(true);
  };

  const closeMenu = ({ returnFocus = false } = {}) => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (returnFocus) {
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
      return;
    }
    openMenu();
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          return;
        }
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          return;
        }
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1,
        );
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (!isOpen) {
          openMenu();
          return;
        }
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value);
        }
        break;
      case "Escape":
        event.preventDefault();
        closeMenu({ returnFocus: true });
        break;
      default:
        break;
    }
  };

  const menuPortal =
    isOpen &&
    menuLayout &&
    createPortal(
      <ul
        ref={menuRef}
        id={`${id}-menu`}
        role="listbox"
        aria-labelledby={label ? `${id}-label` : undefined}
        aria-activedescendant={
          highlightedIndex >= 0 && options[highlightedIndex]
            ? `${id}-option-${options[highlightedIndex].value}`
            : undefined
        }
        tabIndex={-1}
        style={{
          position: "fixed",
          top: menuLayout.top,
          left: menuLayout.left,
          width: menuLayout.width,
          maxHeight: menuLayout.maxHeight,
          zIndex: 9999,
        }}
        className="overflow-y-auto focus:outline-none overflow-x-hidden rounded-xl border border-cfCardBorder bg-cfSecondary py-1 shadow-lg ring-1 ring-cfCardBorder"
      >
        {options.map((opt, index) => {
          const isSelected = opt.value === value;
          return (
            <li
              key={opt.value}
              id={`${id}-option-${opt.value}`}
              role="option"
              aria-selected={isSelected}
            >
              <button
                type="button"
                tabIndex={-1}
                onClick={() => handleSelect(opt.value)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex w-full items-center focus:outline-none justify-between gap-2 px-3.5 py-2.5 text-sm text-left transition-colors duration-150 ${
                  isSelected
                    ? "bg-cfPrimaryMuted text-cfPrimary font-medium"
                    : highlightedIndex === index
                      ? "bg-cfPrimaryMuted/60 text-cfHeadingText"
                      : "text-cfHeadingText hover:bg-cfSecondary"
                }`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && (
                  <Check
                    className="w-4 h-4 shrink-0 text-cfPrimary"
                    strokeWidth={2.5}
                    aria-hidden
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>,
      document.body,
    );

  return (
    <div className="space-y-1.5" ref={rootRef}>
      {label && (
        <label id={`${id}-label`} htmlFor={id} className={LABEL_CLASS}>
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? `${id}-menu` : undefined}
          aria-labelledby={label ? `${id}-label ${id}` : undefined}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className={`${SELECT_TRIGGER_CLASS} inline-flex items-center justify-between gap-2 text-left ${
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:border-cfButton"
          } ${isOpen ? "ring-2 ring-cfPrimary/20 border-cfButton" : ""}`}
        >
          <span className="truncate">{selected?.label ?? ""}</span>
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-cfGlobalText transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </div>
      {menuPortal}
    </div>
  );
}

export default CfFormSelect;
